import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { formatDateTime } from "../lib/format";
import { ComposeDialog } from "../pages/studio/MailClient";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Mail, ShieldCheck, Inbox, Send, RefreshCw, Paperclip, Download, Loader2, Search, CheckCheck, Reply, FolderInput } from "lucide-react";
import { MailAttToPraticaDialog } from "./MailAttToPraticaDialog";
import { toast } from "sonner";

function MsgModal({ msg, onClose, onReply, praticaId, praticaNumero }) {
  const [saveIdx, setSaveIdx] = useState(null);
  const dl = (a) => {
    try {
      const bin = atob(a.content_b64 || "");
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([arr], { type: a.mime || "application/octet-stream" }));
      const link = document.createElement("a"); link.href = url; link.download = a.nome || "allegato"; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch { toast.error("Download non riuscito"); }
  };
  return (
    <Dialog open={!!msg} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-2xl" data-testid="com-msg-modal">
        {msg && (
          <>
            <DialogHeader>
              <DialogTitle className="font-head flex items-center gap-2 text-base">
                {msg.account === "pec" ? <ShieldCheck className="h-4 w-4 text-amber-400" /> : <Mail className="h-4 w-4 text-amber-400" />}
                <span className="text-xs uppercase tracking-wide text-slate-400">{msg.account === "pec" ? "PEC" : "Email"} · {msg.direction === "ricevuta" ? "Ricevuta" : "Inviata"}</span>
                {msg.direction === "ricevuta" && (
                  <Button size="sm" variant="outline" onClick={() => onReply(msg)} data-testid="com-msg-reply" className="ml-auto mr-6 border-slate-700 text-slate-200 hover:bg-slate-800 h-8">
                    <Reply className="h-4 w-4 mr-1" /> Rispondi
                  </Button>
                )}
              </DialogTitle>
            </DialogHeader>
            <div>
              <h3 className="font-head font-semibold text-slate-100">{msg.subject || "(nessun oggetto)"}</h3>
              <div className="mt-2 text-sm text-slate-400 space-y-0.5">
                <div><span className="text-slate-500">Da:</span> {msg.from_name ? `${msg.from_name} · ` : ""}{msg.from_addr || "—"}</div>
                <div><span className="text-slate-500">A:</span> {(msg.to || []).join(", ") || "—"}</div>
                <div><span className="text-slate-500">Data:</span> {formatDateTime(msg.date)}</div>
              </div>
              {(msg.attachments || []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {msg.attachments.map((a, i) => (
                    <div key={i} className="flex items-stretch text-xs text-slate-200 bg-slate-900/60 border border-slate-700 rounded hover:border-amber-500">
                      <button onClick={() => dl(a)} data-testid={`com-msg-att-${i}`} title="Scarica sul PC" className="flex items-center gap-2 px-2.5 py-1.5">
                        <Paperclip className="h-3.5 w-3.5 text-amber-400" /> <span className="max-w-[180px] truncate">{a.nome}</span> <Download className="h-3.5 w-3.5 text-slate-400" />
                      </button>
                      {msg.direction === "ricevuta" && (
                        <button onClick={() => setSaveIdx(i)} data-testid={`com-msg-att-save-${i}`} title="Salva in allegati della pratica"
                          className="flex items-center gap-1 border-l border-slate-700 px-2 text-emerald-400 hover:bg-emerald-500/10">
                          <FolderInput className="h-3.5 w-3.5" /> Salva in allegati
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-4 border-t border-slate-800 pt-4 text-slate-200 text-sm leading-relaxed [&_a]:text-amber-400" data-testid="com-msg-body" dangerouslySetInnerHTML={{ __html: msg.html || "" }} />
            </div>
          </>
        )}
      </DialogContent>
      {saveIdx !== null && (
        <MailAttToPraticaDialog only={saveIdx} account={msg?.account}
          msg={{ ...msg, pratica_id: msg?.pratica_id || praticaId, pratica_numero: msg?.pratica_numero || praticaNumero }}
          onClose={() => setSaveIdx(null)} />
      )}
    </Dialog>
  );
}

export default function PraticaComunicazioni({ praticaId, praticaNumero }) {
  const [data, setData] = useState({ messages: [], unread: 0, cliente_email: "", cliente_pec: "" });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selected, setSelected] = useState(null);
  const [compose, setCompose] = useState(null); // {account, initial}
  const [fAccount, setFAccount] = useState("all"); // all | email | pec
  const [fDir, setFDir] = useState("all"); // all | ricevuta | inviata
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/studio/pratiche/${praticaId}/comunicazioni`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [praticaId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onChange = () => load();
    const onFocus = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("pgp-mail-link-changed", onChange);
    document.addEventListener("visibilitychange", onFocus);
    return () => { window.removeEventListener("pgp-mail-link-changed", onChange); document.removeEventListener("visibilitychange", onFocus); };
  }, [load]);
  // Auto-refresh ogni 5 minuti (stesso ritmo del polling IMAP globale)
  useEffect(() => {
    const id = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [load]);

  const openMsg = async (m) => {
    try {
      const { data } = await api.get(`/studio/mail/${m.account}/messages/${m.id}`);
      setSelected({ ...data, account: m.account, direction: m.direction });
      if (m.direction === "ricevuta" && !m.read) {
        setData((d) => ({ ...d, unread: Math.max(0, d.unread - 1), messages: d.messages.map((x) => (x.id === m.id ? { ...x, read: true } : x)) }));
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const [e, p] = await Promise.all([
        api.post(`/studio/mail/email/sync`).catch(() => ({ data: { new: 0 } })),
        api.post(`/studio/mail/pec/sync`).catch(() => ({ data: { new: 0 } })),
      ]);
      const tot = (e.data?.new || 0) + (p.data?.new || 0);
      if (tot > 0) toast.success(`${tot} nuovi messaggi scaricati`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSyncing(false); }
  };

  const newMsg = (account) => {
    const to = account === "pec" ? data.cliente_pec : data.cliente_email;
    const subject = praticaNumero ? `[PRATICA-${praticaNumero}] ` : "";
    setCompose({ account, initial: { to: to ? [to] : [], subject } });
  };

  const replyMsg = (m) => {
    const s = m.subject || "";
    const escQ = (x) => String(x || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const who = m.from_name ? `${escQ(m.from_name)} &lt;${escQ(m.from_addr)}&gt;` : escQ(m.from_addr);
    const orig = (m.html || "").trim() || `<p>${escQ(m.text || "").replace(/\n/g, "<br>")}</p>`;
    const html = `<p></p><p></p><p>Il ${escQ(formatDateTime(m.date))}, ${who} ha scritto:</p><blockquote>${orig}</blockquote>`;
    setSelected(null);
    setCompose({ account: m.account === "pec" ? "pec" : "email", initial: { to: m.from_addr ? [m.from_addr] : [], subject: /^re:/i.test(s.trim()) ? s : `Re: ${s}`, html } });
  };

  const markAllRead = async () => {
    try {
      await api.post(`/studio/pratiche/${praticaId}/comunicazioni/read-all`);
      setData((d) => ({ ...d, unread: 0, messages: d.messages.map((m) => ({ ...m, read: true })) }));
      toast.success("Tutte le comunicazioni segnate come lette");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const q = query.trim().toLowerCase();
  const filtered = data.messages.filter(
    (m) => (fAccount === "all" || m.account === fAccount) && (fDir === "all" || m.direction === fDir)
      && (!q || (m.subject || "").toLowerCase().includes(q) || (m.counterpart || "").toLowerCase().includes(q))
  );

  return (
    <div className="pgp-card p-5" data-testid="pratica-comunicazioni">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="text-sm font-head font-bold text-amber-400 tracking-wide">COMUNICAZIONI (EMAIL / PEC)</div>
        {data.unread > 0 && (
          <span className="text-[11px] font-mono bg-amber-500 text-[#0a0d14] rounded-full px-2 py-0.5" data-testid="com-unread-badge">{data.unread} non {data.unread === 1 ? "letto" : "letti"}</span>
        )}
        <div className="ml-auto flex items-center gap-2">
          {data.unread > 0 && (
            <Button size="sm" variant="outline" onClick={markAllRead} data-testid="com-mark-all-read" className="border-slate-700 text-slate-200 hover:bg-slate-800 h-8">
              <CheckCheck className="h-4 w-4 mr-1" /> Segna letti
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={sync} disabled={syncing} data-testid="com-sync-btn" className="border-slate-700 text-slate-200 hover:bg-slate-800 h-8">
            {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
          <Button size="sm" onClick={() => newMsg("email")} data-testid="com-new-email" className="h-8 text-white" style={{ backgroundColor: "#38bdf8" }}>
            <Mail className="h-4 w-4 mr-1" /> Nuova Email
          </Button>
          <Button size="sm" onClick={() => newMsg("pec")} data-testid="com-new-pec" className="h-8 text-white" style={{ backgroundColor: "#1e3a8a" }}>
            <ShieldCheck className="h-4 w-4 mr-1" /> Nuova PEC
          </Button>
        </div>
      </div>

      {data.messages.length > 0 && (
        <div className="mb-3 relative" data-testid="com-search-wrap">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="com-search"
            placeholder="Cerca per oggetto o mittente…"
            className="w-full h-9 pl-8 pr-3 rounded-md bg-slate-900/60 border border-slate-700 text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-cyan-500/50" />
        </div>
      )}

      {data.messages.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="com-filters">
          <div className="inline-flex rounded-md overflow-hidden border border-slate-700">
            {[["all", "Tutti"], ["email", "Email"], ["pec", "PEC"]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setFAccount(v)} data-testid={`com-filter-acc-${v}`}
                className={`px-3 py-1 text-xs font-semibold transition-colors ${fAccount === v ? "bg-cyan-500/20 text-cyan-300" : "bg-slate-800/60 text-slate-400 hover:text-slate-200"}`}>{l}</button>
            ))}
          </div>
          <div className="inline-flex rounded-md overflow-hidden border border-slate-700">
            {[["all", "Tutte"], ["ricevuta", "Ricevute"], ["inviata", "Inviate"]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setFDir(v)} data-testid={`com-filter-dir-${v}`}
                className={`px-3 py-1 text-xs font-semibold transition-colors ${fDir === v ? "bg-cyan-500/20 text-cyan-300" : "bg-slate-800/60 text-slate-400 hover:text-slate-200"}`}>{l}</button>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-amber-500" /></div>
      ) : data.messages.length === 0 ? (
        <div className="py-8 text-center text-sm text-slate-500" data-testid="com-empty">Nessuna comunicazione per questa pratica. Usa "Nuova Email" o "Nuova PEC".</div>
      ) : filtered.length === 0 ? (
        <div className="py-8 text-center text-sm text-slate-500" data-testid="com-empty-filter">Nessun messaggio corrisponde ai filtri selezionati.</div>
      ) : (
        <ul className="divide-y divide-slate-800 border border-slate-800 rounded-lg overflow-hidden" data-testid="com-list">
          {filtered.map((m) => {
            const rec = m.direction === "ricevuta";
            const unread = rec && !m.read;
            return (
              <li key={m.id}>
                <button onClick={() => openMsg(m)} data-testid={`com-msg-${m.id}`}
                  className={`w-full text-left px-4 py-2.5 hover:bg-slate-800/40 transition-colors flex items-center gap-3 ${unread ? "bg-amber-500/[0.05]" : ""}`}>
                  <span className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase rounded px-1.5 py-0.5 ${rec ? "bg-emerald-500/15 text-emerald-300" : "bg-sky-500/15 text-sky-300"}`}>
                    {rec ? <Inbox className="h-3 w-3" /> : <Send className="h-3 w-3" />} {rec ? "Ricevuta" : "Inviata"}
                  </span>
                  <span className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase rounded px-1.5 py-0.5 ${m.account === "pec" ? "bg-indigo-500/15 text-indigo-300" : "bg-cyan-500/15 text-cyan-300"}`}>
                    {m.account === "pec" ? "PEC" : "Email"}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className={`truncate text-sm ${unread ? "font-semibold text-slate-100" : "text-slate-300"}`}>{m.subject || "(nessun oggetto)"}</div>
                    <div className="truncate text-xs text-slate-500">{rec ? "Da: " : "A: "}{m.counterpart || "—"}</div>
                    {(m.attachment_names || []).length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1" data-testid={`com-msg-atts-${m.id}`}>
                        {m.attachment_names.map((n, ai) => (
                          <span key={ai} className="inline-flex items-center gap-1 text-[10px] text-slate-400 bg-slate-800/70 border border-slate-700 rounded px-1.5 py-0.5">
                            <Paperclip className="h-3 w-3 text-amber-400" /><span className="max-w-[160px] truncate">{n}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  {m.has_attachments && <Paperclip className="h-3.5 w-3.5 text-slate-500 shrink-0" />}
                  <span className="ml-1 text-[11px] text-slate-500 font-mono shrink-0">{formatDateTime(m.date)}</span>
                  {unread && <span className="h-2 w-2 rounded-full bg-amber-500 shrink-0" data-testid={`com-unread-${m.id}`} />}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <MsgModal msg={selected} onClose={() => setSelected(null)} onReply={replyMsg} praticaId={praticaId} praticaNumero={praticaNumero} />
      {compose && (
        <>
        {compose.initial?.html && <style>{".ProseMirror blockquote{border-left:3px solid #9ca3af;margin:4px 0 4px 2px;padding-left:12px;color:#4b5563}"}</style>}
        <ComposeDialog open onClose={() => setCompose(null)} account={compose.account} initial={compose.initial}
          praticaId={praticaId} onSent={() => { setCompose(null); load(); }} />
        </>
      )}
    </div>
  );
}
